import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/CompaniesManagementPage.jsx
import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Alert,
    CircularProgress,
    Switch,
    InputAdornment
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
    Business as BusinessIcon,
    Assignment as AssignmentIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Link as LinkIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { toast } from 'react-toastify';
import LoadingPage from '../components/common/LoadingPage';

const CompaniesManagementPage = () => {
  const { t } = useTranslation();
    const { user, hasPermission } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [assignDialog, setAssignDialog] = useState({ open: false, company: null });
    const [historyDialog, setHistoryDialog] = useState({ open: false, company: null, history: [] });
    const [linkDialog, setLinkDialog] = useState({ open: false });
    const [generatedLink, setGeneratedLink] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);
    const [assignForm, setAssignForm] = useState({
        plan_id: '',
        start_date: new Date().toISOString().split('T')[0],
        free_trial_days: 0,
        notes: '',
        bill_to: t('hardcoded.pages_billingplanspage.client'),
        show_prices_to_client: true,
        integrator_price: '',
        end_customer_price: '',
        currency: 'USD'
    });

    const [linkForm, setLinkForm] = useState({
        billing_plan_id: '',
        link_type: 'client',
        expires_in_days: 7,
        max_uses: 1,
        bill_to: t('hardcoded.pages_billingplanspage.client'),
        show_prices_to_client: true,
        integrator_company_id: null,
        integrator_price: '',
        end_customer_price: '',
        currency: 'USD'
    });

    // Check if user is Arsenal admin
    const isArsenalAdmin = user?.email?.endsWith(t('hardcoded.components_common_sidebar.arsenalmx')) && hasPermission('admin');
    useEffect(() => {
        if (isArsenalAdmin) {
            loadData();
        }
    }, [isArsenalAdmin]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [companiesRes, plansRes] = await Promise.all([
                api.admin.getAllCompanies({ search }),
                api.billing.listPlans(true) // solo planes activos
            ]);

            setCompanies(companiesRes.data.companies || []);
            setPlans(plansRes.data || []);
        } catch (error) {
            console.error(t('hardcoded.pages_companiesmanagementpage.error_loading_data'), error);
            toast.error(t('hardcoded.pages_companiesmanagementpage.error_al_cargar_datos') + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadData();
    };

    const handleOpenAssignDialog = async (company) => {
        setAssignDialog({ open: true, company });
        const baseForm = {
            plan_id: company.active_plan?.id || '',
            start_date: new Date().toISOString().split('T')[0],
            free_trial_days: 0,
            notes: '',
            bill_to: t('hardcoded.pages_billingplanspage.client'),
            show_prices_to_client: true,
            integrator_price: '',
            end_customer_price: '',
            currency: company.active_plan?.currency || 'USD'
        };

        try {
            const commercialResponse = await api.billing.getCompanyCommercialConfig(company.id);
            const commercial = commercialResponse.data || {};
            setAssignForm({
                ...baseForm,
                bill_to: commercial.bill_to || t('hardcoded.pages_billingplanspage.client'),
                show_prices_to_client: commercial.show_prices_to_client ?? true,
                integrator_price: commercial.integrator_price ?? '',
                end_customer_price: commercial.end_customer_price ?? '',
                currency: commercial.currency || baseForm.currency
            });
        } catch (error) {
            setAssignForm(baseForm);
        }
    };

    const handleAssignPlan = async () => {
        try {
            if (!assignForm.plan_id) {
                toast.error(t('hardcoded.pages_companiesmanagementpage.selecciona_un_plan'));
                return;
            }

            await api.billing.assignPlanToCompany(assignDialog.company.id, {
                plan_id: assignForm.plan_id,
                start_date: assignForm.start_date,
                free_trial_days: assignForm.free_trial_days,
                notes: assignForm.notes
            });

            await api.billing.updateCompanyCommercialConfig(assignDialog.company.id, {
                bill_to: assignForm.bill_to,
                show_prices_to_client: assignForm.show_prices_to_client,
                integrator_price: assignForm.integrator_price === '' ? null : Number(assignForm.integrator_price),
                end_customer_price: assignForm.end_customer_price === '' ? null : Number(assignForm.end_customer_price),
                currency: assignForm.currency,
                pricing_metadata: {
                    source: 'companies_management_page'
                }
            });

            toast.success(t('hardcoded.pages_companiesmanagementpage.plan_asignado_exitosamente'));
            setAssignDialog({ open: false, company: null });
            loadData();
        } catch (error) {
            console.error(t('hardcoded.pages_companiesmanagementpage.error_assigning_plan'), error);
            toast.error(t('hardcoded.pages_companiesmanagementpage.error_al_asignar_plan') + (error.response?.data?.detail || error.message));
        }
    };

    const handleOpenLinkDialog = () => {
        setGeneratedLink('');
        setLinkDialog({ open: true });
        setLinkForm({
            billing_plan_id: plans?.[0]?.id || '',
            link_type: 'client',
            expires_in_days: 7,
            max_uses: 1,
            bill_to: t('hardcoded.pages_billingplanspage.client'),
            show_prices_to_client: true,
            integrator_company_id: '',
            integrator_price: '',
            end_customer_price: '',
            currency: plans?.[0]?.currency || 'USD'
        });
    };

    const handleGenerateRegistrationLink = async () => {
        try {
            if (!linkForm.billing_plan_id) {
                toast.error(t('hardcoded.pages_billingplanspage.selecciona_un_plan_de_facturacion'));
                return;
            }

            if (linkForm.link_type === 'integrator_client' && !linkForm.integrator_company_id) {
                toast.error(t('hardcoded.pages_billingplanspage.selecciona_un_integrador_para_esta'));
                return;
            }

            setLinkLoading(true);

            const payload = {
                billing_plan_id: linkForm.billing_plan_id,
                link_type: linkForm.link_type,
                expires_in_days: Number(linkForm.expires_in_days || 7),
                max_uses: Number(linkForm.max_uses || 1),
                bill_to: linkForm.bill_to,
                show_prices_to_client: !!linkForm.show_prices_to_client,
                integrator_company_id: linkForm.link_type === 'integrator_client'
                    ? linkForm.integrator_company_id
                    : null,
                integrator_price: linkForm.integrator_price === '' ? null : Number(linkForm.integrator_price),
                end_customer_price: linkForm.end_customer_price === '' ? null : Number(linkForm.end_customer_price),
                currency: linkForm.currency
            };

            const response = await api.auth.createRegistrationLink(payload);
            const url = response?.data?.url || '';
            setGeneratedLink(url);
            toast.success(t('hardcoded.pages_billingplanspage.liga_generada_correctamente'));
        } catch (error) {
            console.error('Error generating registration link:', error);
            toast.error(t('hardcoded.pages_billingplanspage.error_al_generar_la_liga') + (error.response?.data?.detail || error.message));
        } finally {
            setLinkLoading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!generatedLink) return;
        try {
            await navigator.clipboard.writeText(generatedLink);
            toast.success(t('hardcoded.pages_billingplanspage.liga_copiada_al_portapapeles'));
        } catch (error) {
            toast.error(t('hardcoded.pages_billingplanspage.no_se_pudo_copiar_la'));
        }
    };

    const handleViewHistory = async (company) => {
        try {
            const response = await api.billing.getCompanyPlanHistory(company.id);
            setHistoryDialog({ open: true, company, history: response.data || [] });
        } catch (error) {
            console.error(t('hardcoded.pages_companiesmanagementpage.error_loading_history'), error);
            toast.error(t('hardcoded.pages_companiesmanagementpage.error_al_cargar_historial') + (error.response?.data?.detail || error.message));
        }
    };

    if (!isArsenalAdmin) {
        return (
            <Container maxWidth={false} disableGutters>
                <Alert severity="error">
                    {t('hardcoded.pages_companiesmanagementpage.solo_administradores_de_arsenal_arsenalmx')}</Alert>
            </Container>
        );
    }

    if (loading) {
        return <LoadingPage message={t('hardcoded.pages_companiesmanagementpage.cargando_companias')} />;
    }

    return (
        <Container maxWidth={false} disableGutters>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                    {t('hardcoded.pages_companiesmanagementpage.gestion_de_companias')}</Typography>

            </Box>

            {/* Search & Actions */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('hardcoded.pages_companiesmanagementpage.buscar_compania_por_nombre_o')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                endAdornment: (
                                    <IconButton size="small" onClick={handleSearch}>
                                        <SearchIcon />
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<LinkIcon />}
                            onClick={handleOpenLinkDialog}
                        >
                            {t('hardcoded.pages_billingplanspage.generar_liga_de_registro')}</Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadData}
                        >
                            {i18n.t('audit.actions.UPDATE')}</Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h3" color="primary">{companies.length}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_companiesmanagementpage.total_companias')}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h3" color="success.main">
                            {companies.filter(c => c.active_plan).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_companiesmanagementpage.con_plan_asignado')}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h3" color="warning.main">
                            {companies.filter(c => !c.active_plan).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_companiesmanagementpage.sin_plan')}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Companies Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow
                            sx={{
                                '&&': { bgcolor: 'error.main' },
                                '&&:hover': { bgcolor: 'error.dark' },
                                '&& th': {
                                    color: '#fff',
                                    fontWeight: 700,
                                    borderBottom: 0,
                                },
                            }}
                        >
                            <TableCell><strong>{t('hardcoded.pages_companiesmanagementpage.compania')}</strong></TableCell>
                            <TableCell><strong>{i18n.t('hardcoded.pages_companiesmanagementpage.contacto')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('vehicles.title')}</strong></TableCell>
                            <TableCell align="center"><strong>{i18n.t('users.title')}</strong></TableCell>
                            <TableCell><strong>{i18n.t('hardcoded.pages_billingplanspage.plan_activo')}</strong></TableCell>
                            <TableCell><strong>{i18n.t('hardcoded.pages_companiesmanagementpage.registro')}</strong></TableCell>
                            <TableCell align="right"><strong>{t('common.actions')}</strong></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {companies.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        {t('hardcoded.pages_companiesmanagementpage.no_se_encontraron_companias')}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            companies.map((company) => (
                                <TableRow key={company.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {company.company_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{company.manager_email}</Typography>
                                        {company.phone && (
                                            <Typography variant="caption" color="text.secondary">
                                                {company.phone}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={company.vehicle_count} size="small" color="primary" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={company.user_count} size="small" color="secondary" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        {company.active_plan ? (
                                            <Box>
                                                <Chip
                                                    label={company.active_plan.name}
                                                    size="small"
                                                    color="success"
                                                />
                                                {company.active_plan.free_trial_ends_at && (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        {t('hardcoded.pages_companiesmanagementpage.trial_hasta')}{new Date(company.active_plan.free_trial_ends_at).toLocaleDateString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Chip label={t('hardcoded.pages_companiesmanagementpage.sin_plan')} size="small" color="warning" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {company.created_at ? new Date(company.created_at).toLocaleDateString() : t('hardcoded.pages_companiesmanagementpage.na')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('hardcoded.pages_companiesmanagementpage.asignar_plan')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleOpenAssignDialog(company)}
                                            >
                                                <AssignmentIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('hardcoded.pages_companiesmanagementpage.ver_historial')}>
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => handleViewHistory(company)}
                                            >
                                                <HistoryIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Assign Plan Dialog */}
            <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, company: null })} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {i18n.t('hardcoded.pages_companiesmanagementpage.asignar_plan_-')}{assignDialog.company?.company_name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {assignDialog.company?.active_plan && (
                            <Alert severity="info">
                                {i18n.t('hardcoded.pages_companiesmanagementpage.plan_actual')}<strong>{assignDialog.company.active_plan.name}</strong>
                            </Alert>
                        )}

                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_companiesmanagementpage.plan_de_facturacion')}</InputLabel>
                            <Select
                                value={assignForm.plan_id}
                                label={t('hardcoded.pages_companiesmanagementpage.plan_de_facturacion')}
                                onChange={(e) => setAssignForm({ ...assignForm, plan_id: e.target.value })}
                            >
                                {plans.map((plan) => (
                                    <MenuItem key={plan.id} value={plan.id}>
                                        {plan.name} ({plan.currency})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            type="date"
                            label={t('hardcoded.pages_companiesmanagementpage.fecha_de_inicio')}
                            value={assignForm.start_date}
                            onChange={(e) => setAssignForm({ ...assignForm, start_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_companiesmanagementpage.dias_de_prueba_gratis')}
                            value={assignForm.free_trial_days}
                            onChange={(e) => setAssignForm({ ...assignForm, free_trial_days: parseInt(e.target.value) || 0 })}
                            inputProps={{ min: 0, max: 365 }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t('hardcoded.components_admin_ordermanagement.notas')}
                            value={assignForm.notes}
                            onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                            placeholder={t('hardcoded.pages_companiesmanagementpage.notas_adicionales_sobre_la_asignacion')}
                        />

                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_billingplanspage.quien_paga')}</InputLabel>
                            <Select
                                value={assignForm.bill_to}
                                label={t('hardcoded.pages_billingplanspage.quien_paga')}
                                onChange={(e) => setAssignForm({ ...assignForm, bill_to: e.target.value })}
                            >
                                <MenuItem value="client">{i18n.t('hardcoded.pages_billingplanspage.cliente_final')}</MenuItem>
                                <MenuItem value="integrator">{t('hardcoded.pages_billingplanspage.integrador')}</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={assignForm.show_prices_to_client}
                                    onChange={(e) => setAssignForm({ ...assignForm, show_prices_to_client: e.target.checked })}
                                />
                            }
                            label={t('hardcoded.pages_billingplanspage.mostrar_precios_al_cliente_final')}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billingplanspage.precio_para_integrador')}
                            value={assignForm.integrator_price}
                            onChange={(e) => setAssignForm({ ...assignForm, integrator_price: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billingplanspage.precio_para_cliente_final')}
                            value={assignForm.end_customer_price}
                            onChange={(e) => setAssignForm({ ...assignForm, end_customer_price: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            label={t('settings.company.currency')}
                            value={assignForm.currency}
                            onChange={(e) => setAssignForm({ ...assignForm, currency: e.target.value.toUpperCase() })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialog({ open: false, company: null })}>
                        {t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleAssignPlan}>
                        {i18n.t('hardcoded.pages_companiesmanagementpage.asignar_plan')}</Button>
                </DialogActions>
            </Dialog>

            {/* Registration Link Dialog */}
            <Dialog
                open={linkDialog.open}
                onClose={() => setLinkDialog({ open: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t('hardcoded.pages_companiesmanagementpage.generar_liga_de_registro_para')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_companiesmanagementpage.plan_de_facturacion')}</InputLabel>
                            <Select
                                value={linkForm.billing_plan_id}
                                label={t('hardcoded.pages_companiesmanagementpage.plan_de_facturacion')}
                                onChange={(e) => setLinkForm({ ...linkForm, billing_plan_id: e.target.value })}
                            >
                                {plans.map((plan) => (
                                    <MenuItem key={plan.id} value={plan.id}>
                                        {plan.name} ({plan.currency})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_billingplanspage.tipo_de_liga')}</InputLabel>
                            <Select
                                value={linkForm.link_type}
                                label={t('hardcoded.pages_billingplanspage.tipo_de_liga')}
                                onChange={(e) => setLinkForm({ ...linkForm, link_type: e.target.value })}
                            >
                                <MenuItem value="client">{i18n.t('hardcoded.pages_billingplanspage.cliente_directo')}</MenuItem>
                                <MenuItem value="integrator_client">{t('hardcoded.pages_billingplanspage.cliente_de_integrador')}</MenuItem>
                            </Select>
                        </FormControl>

                        {linkForm.link_type === 'integrator_client' && (
                            <FormControl fullWidth>
                                <InputLabel>{t('hardcoded.pages_companiesmanagementpage.integrador')}</InputLabel>
                                <Select
                                    value={linkForm.integrator_company_id}
                                    label={t('hardcoded.pages_companiesmanagementpage.integrador')}
                                    onChange={(e) => setLinkForm({ ...linkForm, integrator_company_id: e.target.value })}
                                >
                                    {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            {company.company_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.expira_en_dias')}
                                    value={linkForm.expires_in_days}
                                    onChange={(e) => setLinkForm({ ...linkForm, expires_in_days: e.target.value })}
                                    inputProps={{ min: 1, max: 365 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.usos_maximos')}
                                    value={linkForm.max_uses}
                                    onChange={(e) => setLinkForm({ ...linkForm, max_uses: e.target.value })}
                                    inputProps={{ min: 1, max: 9999 }}
                                />
                            </Grid>
                        </Grid>

                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_billingplanspage.quien_paga')}</InputLabel>
                            <Select
                                value={linkForm.bill_to}
                                label={t('hardcoded.pages_billingplanspage.quien_paga')}
                                onChange={(e) => setLinkForm({ ...linkForm, bill_to: e.target.value })}
                            >
                                <MenuItem value="client">{i18n.t('hardcoded.pages_billingplanspage.cliente_final')}</MenuItem>
                                <MenuItem value="integrator">{t('hardcoded.pages_billingplanspage.integrador')}</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={linkForm.show_prices_to_client}
                                    onChange={(e) => setLinkForm({ ...linkForm, show_prices_to_client: e.target.checked })}
                                />
                            }
                            label={t('hardcoded.pages_billingplanspage.mostrar_precios_al_cliente_final')}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billingplanspage.precio_para_integrador')}
                            value={linkForm.integrator_price}
                            onChange={(e) => setLinkForm({ ...linkForm, integrator_price: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billingplanspage.precio_para_cliente_final')}
                            value={linkForm.end_customer_price}
                            onChange={(e) => setLinkForm({ ...linkForm, end_customer_price: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            label={t('settings.company.currency')}
                            value={linkForm.currency}
                            onChange={(e) => setLinkForm({ ...linkForm, currency: e.target.value.toUpperCase() })}
                        />

                        {generatedLink && (
                            <TextField
                                fullWidth
                                label={t('hardcoded.pages_billingplanspage.liga_generada')}
                                value={generatedLink}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleCopyLink}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinkDialog({ open: false })}>
                        {t('common.close')}</Button>
                    <Button variant="contained" onClick={handleGenerateRegistrationLink} disabled={linkLoading}>
                        {linkLoading ? t('hardcoded.pages_billingplanspage.generando') : t('hardcoded.pages_billingplanspage.generar_liga')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, company: null, history: [] })} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t('hardcoded.pages_companiesmanagementpage.historial_de_planes_-')}{historyDialog.company?.company_name}
                </DialogTitle>
                <DialogContent>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>{i18n.t('hardcoded.components_auth_register.plan')}</strong></TableCell>
                                    <TableCell><strong>{i18n.t('gps.card.assigned')}</strong></TableCell>
                                    <TableCell><strong>{t('hardcoded.components_settings_notificationpreferenceseditor.inicio')}</strong></TableCell>
                                    <TableCell><strong>{t('pages_companiesmanagementpage.table_end')}</strong></TableCell>
                                    <TableCell><strong>{i18n.t('gps.table.status')}</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyDialog.history.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                {t('hardcoded.pages_companiesmanagementpage.no_hay_historial_de_planes')}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    historyDialog.history.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{item.plan_name || t('hardcoded.pages_companiesmanagementpage.na')}</TableCell>
                                            <TableCell>{item.assigned_at ? new Date(item.assigned_at).toLocaleDateString() : t('hardcoded.pages_companiesmanagementpage.na')}</TableCell>
                                            <TableCell>{item.start_date ? new Date(item.start_date).toLocaleDateString() : t('hardcoded.pages_companiesmanagementpage.na')}</TableCell>
                                            <TableCell>{item.end_date ? new Date(item.end_date).toLocaleDateString() : t('common.active')}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.is_active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                                                    size="small"
                                                    color={item.is_active ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialog({ open: false, company: null, history: [] })}>
                        {t('common.close')}</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CompaniesManagementPage;
